import FloatingPopper from "@components/floatingPopper/FloatingPopper";
import { POPOVER_INPUT_SHARP_LEFT_OFFSET_X } from "@components/popover/Popover.utils";
import { offset, shift } from "@floating-ui/react-dom";
import { composeRefHandlers, isHex } from "@utils/general";
import { getZoomLevel } from "@utils/zoom.utils";
import { parseToHsl } from "polished";
import React, { ReactElement, useCallback, useMemo, useRef, useState } from "react";
import { DefaultTheme, useTheme } from "styled-components";

import { BasicInputSizes, IconSize } from "../../../enums";
import useHideOnBlur from "../../../hooks/HideOnBlurHook";
import { KeyName } from "../../../keyName";
import { IAuditTrailData } from "../../../model/Model";
import TestIds from "../../../testIds";
import { CaretIcon, CheckIcon } from "../../icon";
import Input, { IValueInputComponentProps } from "../input";
import { IconDelimiter, IconWrapper } from "../input/Input.styles";
import { Circle, MAX_COLORS_IN_LINE, StyledMenu, StyledSelect } from "./ColorPicker.styles";

interface IProps extends IValueInputComponentProps {
    colors: string[];
    value?: string;
    onChange?: (color: string) => void;
    auditTrailData?: IAuditTrailData;
}

const ColorPicker: React.FC<IProps> = ({ colors, value, onChange, ...props }) => {
    const [focusedValue, setFocusedValue] = useState(value ?? "");
    const theme = useTheme();
    const colorInputRef = useRef<HTMLInputElement>(null);
    const refOpener = useRef<HTMLInputElement>(null);
    const refMenu = useRef<HTMLDivElement>(null);

    const { isVisible, setIsVisible } = useHideOnBlur({
        refs: [refOpener, refMenu],
        initialIsVisible: false
    });

    const handleMouseDown = useCallback((e) => {
        setIsVisible(!isVisible);
        colorInputRef.current?.focus();
        e.preventDefault();
        e.stopPropagation();
    }, [isVisible, setIsVisible]);

    const select = useCallback((color: string) => {
        setFocusedValue(color);
        onChange?.(color);
        setIsVisible(false);
    }, [onChange, setIsVisible, setFocusedValue]);

    const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
        const index = colors.indexOf(focusedValue);

        let nextColor = index,
            linePosition;
        switch (e.key) {
            case KeyName.ArrowRight:
                nextColor = index + 1 === colors.length ? 0 : index + 1;
                break;
            case KeyName.ArrowLeft:
                nextColor = index === 0 ? colors.length - 1 : index - 1;
                break;
            case KeyName.ArrowUp:
                if ((colors.length % MAX_COLORS_IN_LINE) < (index % MAX_COLORS_IN_LINE)) {
                    linePosition = colors.length - ((colors.length % MAX_COLORS_IN_LINE) + MAX_COLORS_IN_LINE - index);
                } else {
                    linePosition = colors.length - ((colors.length % MAX_COLORS_IN_LINE) - index);
                }
                if (linePosition < index) {
                    linePosition = index;
                }
                nextColor = index - MAX_COLORS_IN_LINE < 0 ? linePosition : index - MAX_COLORS_IN_LINE;
                break;
            case KeyName.ArrowDown:
                linePosition = index % MAX_COLORS_IN_LINE;
                if (linePosition > index) {
                    linePosition = index;
                }
                nextColor = index + MAX_COLORS_IN_LINE >= colors.length ? linePosition : index + MAX_COLORS_IN_LINE;
                break;
            case KeyName.Enter:
                if (isVisible) {
                    select(focusedValue);
                }
                setIsVisible(!isVisible);
                break;
            case KeyName.Space:
                setIsVisible(!isVisible);
                break;
            case KeyName.Escape:
                setIsVisible(false);
                break;
        }
        setFocusedValue(colors[nextColor]);
    }, [colors, focusedValue, isVisible, select, setIsVisible]);

    const isDisabled = useMemo(() => {
        return props.isDisabled || props.isReadOnly;
    }, [props.isDisabled, props.isReadOnly]);

    // get dark checkIcon for light colors and vice versa
    const getCheckIconColor = useCallback((color: string) => {
        if (!isHex(color)) {
            color = theme[color as keyof DefaultTheme];
        }
        const HslColor = parseToHsl(color);
        return (HslColor.lightness > 0.5) ? "C_ACT_main" : "C_BG_selected_color";
    }, [theme]);

    const renderMenu = (): ReactElement => {
        return <FloatingPopper
                referenceElement={refOpener?.current}
                placement={"bottom-start"}
                useZoomCorrection={false}
                modifiers={[
                    offset({ crossAxis: POPOVER_INPUT_SHARP_LEFT_OFFSET_X * getZoomLevel() }),
                    shift({ padding: 19, rootBoundary: "viewport" }),
                ]}
        >
            {(popperProps) => (
                    <StyledMenu data-testid={TestIds.ColorPickerMenu}
                                ref={composeRefHandlers(popperProps.ref, refMenu)}
                                style={popperProps.style}
                                data-popper-placement={popperProps.placement}>
                        {colors.map(color => {
                            return <Circle key={color}
                                           data-testid={TestIds.Color}
                                           onMouseDown={() => select(color)}
                                           selected={color === value}
                                           $focused={color === focusedValue}
                                           color={color}>
                                {color === value
                                        &&
                                        <CheckIcon width={IconSize.S} color={getCheckIconColor(color)}
                                                   preventHover/>}
                            </Circle>;
                        })}
                    </StyledMenu>
            )}
        </FloatingPopper>;
    };

    if (props.isSkeleton) {
        return <ColorPickerSkeleton/>;
    }

    return <StyledSelect ref={refOpener}
                         auditTrailType={props.auditTrailData?.type}
                         data-testid={TestIds.ColorPicker}
                         onMouseDown={handleMouseDown}
                         isDisabled={isDisabled}
    >
        <Circle color={value}
                data-testid={TestIds.Color}/>
        <IconWrapper
                isActive={isVisible}>
            <IconDelimiter/>
            <CaretIcon width={IconSize.M}/>
        </IconWrapper>
        <input ref={colorInputRef}
               tabIndex={isDisabled ? -1 : 0}
               onKeyDown={handleKeyDown}
        />
        {isVisible && renderMenu()}
    </StyledSelect>;
};

export default ColorPicker;

const ColorPickerSkeleton: React.FunctionComponent = () => {
    return <Input isSkeleton width={BasicInputSizes.XS}/>;
};